/**
 * Heizkostenabrechnung nach HeizkostenV.
 *
 *  § 7 Abs. 1: 50–70 % der Kosten sind verbrauchsabhängig zu verteilen, der Rest nach
 *              Wohn-/Nutzfläche (Grundkosten).
 *  § 9:        Bei verbundenen Anlagen wird die Wärmemenge für Warmwasser abgetrennt.
 *  § 12:       Wird nicht verbrauchsabhängig abgerechnet, darf der Mieter um 15 % kürzen.
 *              -> `pruefeHeizkostenV()` liefert die Warnung, bevor festgeschrieben wird.
 *
 * CO2-Kosten: Der Vermieteranteil nach CO2KostAufG mindert die umlagefähigen Heizkosten,
 * bevor verteilt wird. Er wird in der Abrechnung gesondert ausgewiesen (Pflicht).
 */

import { Cent, verteileNachGewichten, teileProzent } from './geld';
import { Zeitraum, tage } from './zeit';
import { Belegungsabschnitt, Einheit, UmlageZeile } from './umlage';
import { AnlagenZustand, Co2Aufteilung, teileCo2Kosten } from './co2';

export const VERBRAUCHSANTEIL_MIN = 50;
export const VERBRAUCHSANTEIL_MAX = 70;
export const KUERZUNGSRECHT_PROZENT = 15;

export interface HeizkostenEingabe {
  /** Gesamtkosten Heizung inkl. Warmwasser und inkl. CO2-Kosten, in Cent. */
  gesamtkostenCent: Cent;
  /** In den Gesamtkosten enthaltene CO2-Kosten, in Cent. */
  co2KostenCent: Cent;
  /** CO2-Menge des Objekts im Zeitraum, in kg. Für den Emissionskennwert. */
  co2MengeKg?: number;
  /** Verbrauchsabhängiger Anteil in Prozent, 50..70. Default 70. */
  verbrauchAnteilProzent?: number;
  /** Verbrauch je Belegungsabschnitt (kWh oder Verbrauchseinheiten). */
  verbrauchJeAbschnitt: Record<string, number>;
  /** Emissionskennwert kg CO2/m²/a. Nötig, solange das Stufenmodell greift. */
  co2Kennwert?: number;
  /** Brennstoff und Einbauzustand — entscheidet über Stufenmodell vs. paritätische Teilung. */
  anlage?: AnlagenZustand;
  /** In den Gesamtkosten enthaltene Gasnetzentgelte. Ab 2028 ggf. hälftig Vermieter. */
  netzentgelteCent?: Cent;
  /** In den Gesamtkosten enthaltene Bioanteil-Mehrkosten. Ab 2029 ggf. hälftig Vermieter. */
  bioanteilMehrkostenCent?: Cent;
  abrechnungsjahr: number;
}

export interface HeizkostenErgebnis {
  co2: Co2Aufteilung;
  /** Auf die Mieterschaft umlagefähige Heizkosten nach Abzug des CO2-Vermieteranteils. */
  umlagefaehigCent: Cent;
  verbrauchsteilCent: Cent;
  grundkostenteilCent: Cent;
  verbrauchAnteilProzent: number;
  zeilen: UmlageZeile[];
  warnungen: string[];
}

export function berechneHeizkosten(
  eingabe: HeizkostenEingabe,
  abschnitte: Belegungsabschnitt[],
  einheiten: Einheit[],
  zeitraum: Zeitraum,
): HeizkostenErgebnis {
  const verbrauchAnteilProzent = eingabe.verbrauchAnteilProzent ?? 70;
  if (
    verbrauchAnteilProzent < VERBRAUCHSANTEIL_MIN ||
    verbrauchAnteilProzent > VERBRAUCHSANTEIL_MAX
  ) {
    throw new Error(
      `Verbrauchsabhängiger Anteil muss zwischen ${VERBRAUCHSANTEIL_MIN} und ` +
        `${VERBRAUCHSANTEIL_MAX} Prozent liegen (HeizkostenV § 7 Abs. 1), war ` +
        `${verbrauchAnteilProzent}.`,
    );
  }

  const warnungen: string[] = [];
  const zeitraumTage = tage(zeitraum);
  const byId = new Map(einheiten.map((e) => [e.id, e]));

  // 1) CO2-Kosten aufteilen. Der Vermieteranteil verlässt die Umlagemasse.
  const co2 = teileCo2Kosten({
    co2KostenCent: eingabe.co2KostenCent,
    abrechnungsjahr: eingabe.abrechnungsjahr,
    kennwert: eingabe.co2Kennwert,
    anlage: eingabe.anlage,
    netzentgelteCent: eingabe.netzentgelteCent,
    bioanteilMehrkostenCent: eingabe.bioanteilMehrkostenCent,
  });

  const umlagefaehigCent =
    eingabe.gesamtkostenCent -
    co2.vermieterAnteilCent -
    co2.netzentgeltVermieterCent -
    co2.bioanteilVermieterCent;

  // 2) Verbrauchsteil / Grundkosten trennen.
  const { a: verbrauchsteilCent, b: grundkostenteilCent } = teileProzent(
    umlagefaehigCent,
    verbrauchAnteilProzent,
  );

  // 3) Verbrauchsteil nach gemessenem Verbrauch.
  const verbrauchGewichte = abschnitte.map((a) => {
    const v = eingabe.verbrauchJeAbschnitt[a.id];
    if (v === undefined) {
      throw new Error(`Für Abschnitt ${a.id} fehlt der Heizungsverbrauch.`);
    }
    return v;
  });
  const verbrauchSumme = verbrauchGewichte.reduce((x, y) => x + y, 0);
  if (verbrauchSumme === 0) {
    warnungen.push(
      'Es liegen keine Verbrauchswerte vor. Eine Abrechnung ohne verbrauchsabhängige ' +
        `Verteilung berechtigt den Mieter nach § 12 HeizkostenV zu einer Kürzung um ` +
        `${KUERZUNGSRECHT_PROZENT} Prozent.`,
    );
  }
  const verbrauchAnteile =
    verbrauchSumme === 0
      ? abschnitte.map(() => 0)
      : verteileNachGewichten(verbrauchsteilCent, verbrauchGewichte);

  // 4) Grundkosten nach Wohnfläche, tagegenau zeitgewichtet.
  const flaechenGewichte = abschnitte.map((a) => {
    const e = byId.get(a.einheitId);
    if (!e) throw new Error(`Unbekannte Einheit ${a.einheitId}`);
    return e.wohnflaecheQm * (a.tage / zeitraumTage);
  });
  const grundAnteile = verteileNachGewichten(grundkostenteilCent, flaechenGewichte);

  // Bei fehlenden Verbrauchswerten läuft der Verbrauchsteil sonst ins Leere.
  const restCent =
    verbrauchsteilCent - verbrauchAnteile.reduce((x, y) => x + y, 0);
  const restVerteilt =
    restCent === 0 ? abschnitte.map(() => 0) : verteileNachGewichten(restCent, flaechenGewichte);

  const flaechenSumme = flaechenGewichte.reduce((x, y) => x + y, 0);

  const zeilen: UmlageZeile[] = [];
  abschnitte.forEach((a, i) => {
    zeilen.push({
      abschnittId: a.id,
      einheitId: a.einheitId,
      mietverhaeltnisId: a.mietverhaeltnisId,
      kostenartCode: 'heizung_verbrauch',
      schluessel: 'verbrauch',
      gesamtkostenCent: verbrauchsteilCent,
      gesamtBasis: r3(verbrauchSumme),
      anteilBasis: r3(verbrauchGewichte[i]),
      zeitanteilTage: a.tage,
      zeitraumTage,
      anteilCent: verbrauchAnteile[i] + restVerteilt[i],
      vermieterTraegt: a.vermieterTraegt,
    });
    zeilen.push({
      abschnittId: a.id,
      einheitId: a.einheitId,
      mietverhaeltnisId: a.mietverhaeltnisId,
      kostenartCode: 'heizung_grundkosten',
      schluessel: 'wohnflaeche',
      gesamtkostenCent: grundkostenteilCent,
      gesamtBasis: r3(flaechenSumme),
      anteilBasis: r3(flaechenGewichte[i]),
      zeitanteilTage: a.tage,
      zeitraumTage,
      anteilCent: grundAnteile[i],
      vermieterTraegt: a.vermieterTraegt,
    });
  });

  return {
    co2,
    umlagefaehigCent,
    verbrauchsteilCent,
    grundkostenteilCent,
    verbrauchAnteilProzent,
    zeilen,
    warnungen,
  };
}

/**
 * Warmwasser-Wärmemenge bei verbundener Anlage ohne Wärmezähler, § 9 Abs. 2 HeizkostenV.
 *
 * Gesetzeswortlaut: Q = 2,5 · V · (tw − 10)  [kWh], V in m³, tw in °C (Default 60).
 * Der Faktor 2,5 trägt die Einheit kWh/(m³·K) bereits in sich (inkl. Verteil-/Zirkulations-
 * verluste). KEINE Division durch 1000 — die ergäbe (bei V in m³) ein um Faktor 1000 zu
 * kleines Ergebnis. Beispiel: V = 1 m³, tw = 60 °C → 2,5 · 1 · 50 = 125 kWh.
 */
export function warmwasserWaermemengeKwh(volumenM3: number, tempC = 60): number {
  if (volumenM3 < 0) throw new Error('Warmwasservolumen darf nicht negativ sein');
  return 2.5 * volumenM3 * (tempC - 10);
}

/** Formelle Prüfung vor dem Festschreiben. Leeres Array = keine Beanstandung. */
export function pruefeHeizkostenV(e: HeizkostenErgebnis): string[] {
  const probleme = [...e.warnungen];
  if (
    e.verbrauchAnteilProzent < VERBRAUCHSANTEIL_MIN ||
    e.verbrauchAnteilProzent > VERBRAUCHSANTEIL_MAX
  ) {
    probleme.push(
      `Verbrauchsanteil ${e.verbrauchAnteilProzent} % liegt außerhalb von ` +
        `${VERBRAUCHSANTEIL_MIN}–${VERBRAUCHSANTEIL_MAX} % (§ 7 Abs. 1 HeizkostenV).`,
    );
  }
  return probleme;
}

function r3(n: number): number {
  return Math.round(n * 1000) / 1000;
}
